// Homepage content. Every claim below is lifted from existing KUR site copy.
// Edit here, not in the components.

import sponsorsImg from "../ASSETS/IMAGES/sponsorsHero.jpg";
import physiciansImg from "../ASSETS/IMAGES/physiciansHero.jpg";
import patientsImg from "../ASSETS/IMAGES/patientsHero.jpg";
import iconRD from "../ASSETS/ICONS/physiciansResearchDevepolment.svg"; // magnifier (file name is misspelled)
import iconContracts from "../ASSETS/ICONS/physiciansContractsIcon.svg";
import iconManagement from "../ASSETS/ICONS/physiciansResearchManagement.svg";
import iconCTMS from "../ASSETS/ICONS/physiciansCTManagement.svg";
import iconMarketing from "../ASSETS/ICONS/physiciansMarketingEnrollment.svg";

export const hero = {
  title: "Making progress possible.",
  lead: "KUR Research is an integrated clinical research site network with over a decade of experience and a proven track record of success from hundreds of clinical trials. We partner with established, community-based private practices, urgent cares, and specialists, allowing us to recruit diverse patient populations.",
  primary: { label: "For Sponsors", href: "/sponsors" },
  secondary: { label: "Partner With KUR", href: "/physicians" },
  tertiary: { label: "Explore clinical trials", href: "/patients" },
  seal: "We have been designated as a Gene Therapy Ready site by Advarra, the IBC of record for the NIH. This demonstrates and bolsters our ability to conduct clinical trials involving genetically-engineered vaccines and therapies.",
};

export const statistics = [
  { value: 11, suffix: "", label: "Years of research experience" },
  { value: 823, suffix: "+", label: "Studies" },
  { value: 120, suffix: "+", label: "Sponsors" },
  { value: 18, suffix: "", label: "Sites" },
  { value: 54, suffix: "", label: "Investigators" },
];

export const audiences = [
  {
    id: "sponsors",
    label: "Sponsors",
    title: "Accelerate clinical research through an integrated site network.",
    description:
      "Direct access to investigators with diverse patient populations across multiple therapeutic specialties and geographies. A seamless experience in the advancement of clinical research through quality data, reliability, and fast enrollment.",
    benefits: [
      "Site selection to first subject enrolled in as fast as 10 days",
      "Diverse patient populations across multiple specialties and geographies",
      "Contracts, regulatory and quality centralized for consistency",
    ],
    cta: { label: "View sponsor capabilities", href: "/sponsors" },
    image: sponsorsImg,
    alt: "Clinical research team at a KUR site",
  },
  {
    id: "physicians",
    label: "Physicians",
    title: "Bring clinical research into your existing practice.",
    description:
      "We provide our Physicians with Dedicated Research Infrastructure at no cost and an opportunity to aid in the development of the most advanced treatments.",
    benefits: [
      "Dedicated research infrastructure at no cost",
      "KUR negotiates contracts and budgets and handles administration",
      "KUR research staff facilitate and manage each trial",
    ],
    cta: { label: "Partner with KUR", href: "/physicians" },
    image: physiciansImg,
    alt: "Physician in a KUR partner practice",
  },
  {
    id: "patients",
    label: "Patients",
    title: "Explore opportunities to take part in research.",
    description:
      "We improve our Patients lives by providing access to advanced medical treatments, superior healthcare oversight, and higher education about medical conditions.",
    benefits: [
      "Access to advanced medical treatments",
      "Superior healthcare oversight",
      "Education about medical conditions",
    ],
    cta: { label: "Explore clinical trials", href: "/patients" },
    image: patientsImg,
    alt: "Patient speaking with a clinician",
  },
];

export const difference = {
  eyebrow: "The KUR difference",
  title: "One network. One standard. Every site.",
  lead: "KUR pairs community-based practices with centralized operations, so sponsors get local access to patients and consistent quality across every protocol.",
  points: [
    {
      figure: "10",
      unit: "days",
      title: "From site selection to first subject",
      text: "As fast as, based on the needs of the sponsor.",
    },
    {
      figure: "54",
      unit: "investigators",
      title: "Experienced, multi-specialty",
      text: "Direct access to investigators across therapeutic specialties.",
    },
    {
      figure: "1",
      unit: "corporate office",
      title: "Centralized solutions",
      text: "Relevant processes are centralized to promote quality and consistency across sites and protocols.",
    },
    {
      figure: "Community",
      unit: "based",
      title: "Diverse patient populations",
      text: "Private practices, urgent cares and specialists allow us to recruit diverse patient populations.",
    },
  ],
};

export const lifecycle = [
  {
    title: "Site identification",
    text: "Site identification feasibility and clinical protocol evaluation.",
  },
  {
    title: "Feasibility",
    text: "CDA execution and feasibility questionnaire completion and submission.",
  },
  {
    title: "Study startup",
    text: "Budget and contract negotiation, regulatory submission, IRB/IEC and SIV scheduling. As fast as 10 days to first subject.",
  },
  {
    title: "Recruitment",
    text: "Subject engagement and recruitment through community-based practices, supported by site marketing.",
  },
  {
    title: "Study execution",
    text: "Informed consent, staff training, investigational product and supply management, AE/SAE processing.",
  },
  {
    title: "Quality and data",
    text: "QA/QC, data management and EDC entry, query resolution, monitoring and audit support.",
  },
  {
    title: "Closeout",
    text: "Study close out, patient data and site file archiving, and the full billing cycle.",
  },
];

export const capabilities = [
  {
    id: "rd",
    label: "Research & Development",
    title: "Trials matched to your patient population.",
    text: "KUR brings each practice clinical trials relevant to its patient population.",
    points: ["Site identification feasibility", "Clinical protocol evaluation", "Protocol management assignment"],
    icon: iconRD,
    href: "/sponsors#solutions",
  },
  {
    id: "contracts",
    label: "Contracts & Administrative",
    title: "Every contract and budget, handled centrally.",
    text: "KUR negotiates all contracts and budgets and performs all administrative duties.",
    points: ["CDA execution", "Budget and contract negotiation", "Regulatory activities and IRB/IEC submission"],
    icon: iconContracts,
    href: "/sponsors#solutions",
  },
  {
    id: "management",
    label: "Research Site Management",
    title: "Research staff who run each trial.",
    text: "KUR employs research staff to facilitate and manage each clinical trial.",
    points: ["Staff training", "Common SOPs", "Monitoring and audit support"],
    icon: iconManagement,
    href: "/physicians#management",
  },
  {
    id: "ctms",
    label: "Clinical Trial Management",
    title: "One proprietary system for every process and data point.",
    text: "All of KUR's processes and data are managed by our proprietary IT systems.",
    points: ["IT and CTMS infrastructure", "Data management and EDC entry", "Query resolution"],
    icon: iconCTMS,
    href: "/sponsors#solutions",
  },
  {
    id: "marketing",
    label: "Marketing & Enrollment",
    title: "Campaigns built for each trial.",
    text: "KUR performs marketing campaigns for each trial and assists in patient enrollment.",
    points: ["Subject engagement and recruitment", "Clinical site business development"],
    icon: iconMarketing,
    href: "/patients",
  },
];

export const finalCta = {
  title: "Advancing research takes the right people, the right network and the right infrastructure.",
  primary: { label: "Talk to KUR", href: "/contact" },
  paths: [
    { label: "Sponsors", href: "/sponsors", text: "Site network, startup and quality data" },
    { label: "Physicians", href: "/physicians", text: "Research infrastructure at no cost" },
    { label: "Patients", href: "/patients", text: "Learn about clinical trials" },
  ],
};
